import { Empty, Space, Table, Tag, Typography } from 'antd';

import ProductImage from '../../../components/productImage';
import { formatCurrency, formatKg, formatVolume } from '../../../utils/format';
import './style.less';

const { Text } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 320;

const IMAGE_SHADOW = '0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06)';

/** Тоо болгож хөрвүүлнэ, болохгүй бол 0 (NaN гарахаас хамгаална) */
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Жагсаалтаас сонгосон талбарын нийлбэр */
const sumBy = (list = [], pick) => list.reduce((total, item) => total + num(pick(item)), 0);

/** Хоёр мөр текст: дээр үндсэн, доор нэмэлт (жижиг, сааралдуу) */
const TwoLineText = ({ primary, secondary, align = 'start', maxWidth }) => (
  <Space
    direction="vertical"
    size={0}
    style={{ maxWidth, alignItems: align === 'end' ? 'flex-end' : undefined }}
  >
    <Text style={{ maxWidth }} ellipsis={maxWidth ? { tooltip: primary } : false}>
      {primary}
    </Text>
    <Text type="secondary" style={{ fontSize: 12, maxWidth }} ellipsis>
      {secondary}
    </Text>
  </Space>
);

const VolumeText = ({ value }) => (
  <>
    {`${formatVolume(value)} см`}
    <sup>3</sup>
  </>
);

/**
 * Буцаалтын барааны хүснэгт.
 * Хүргэлт/Татан авалтын дэлгэрэнгүй дээрх хүснэгттэй ижил төрхтэй.
 */
const ReturnProductTable = ({ products = [] }) => {
  const columns = [
    {
      key: 'index',
      title: '№',
      width: 48,
      align: 'center',
      render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
    },
    {
      key: 'product',
      title: 'Бараа',
      render: (_, record) => (
        <Space size={12} align="center">
          <ProductImage
            src={record?.productImage}
            alt={record?.productTitle ?? 'Барааны зураг'}
            size={IMAGE_SIZE}
            style={{ objectFit: 'cover', borderRadius: 4, boxShadow: IMAGE_SHADOW }}
          />
          <TwoLineText
            primary={record?.productTitle}
            secondary={record?.productSKU}
            maxWidth={TITLE_MAX_WIDTH}
          />
        </Space>
      ),
    },
    {
      key: 'total',
      title: 'Дүн / тоо',
      align: 'right',
      width: 140,
      render: (_, record) => (
        <TwoLineText
          align="end"
          primary={record?.totalPrice ? formatCurrency(record.totalPrice) : '-'}
          secondary={`${num(record?.requestedTotalUnit)} ширхэг`}
        />
      ),
    },
    {
      key: 'weight',
      title: 'Жин / эзэлхүүн',
      align: 'right',
      width: 140,
      render: (_, record) => (
        <TwoLineText
          align="end"
          primary={`${formatKg(record?.totalWeight)} кг`}
          secondary={<VolumeText value={record?.totalVolume} />}
        />
      ),
    },
    {
      key: 'type',
      title: 'Төрөл',
      width: 130,
      align: 'center',
      render: (_, record) =>
        record?.productReturnType === 'productNotChange' ? (
          <Tag color="red">Буцаах бараа</Tag>
        ) : (
          <Tag color="blue">Солих бараа</Tag>
        ),
    },
  ];

  /** Нийт дүн, тоо, жин, эзэлхүүн */
  const renderSummary = (rows = []) => (
    <Table.Summary.Row>
      <Table.Summary.Cell colSpan={2} />
      <Table.Summary.Cell align="right">
        <TwoLineText
          align="end"
          primary={<Text strong>{formatCurrency(sumBy(rows, (row) => row.totalPrice))}</Text>}
          secondary={`${sumBy(rows, (row) => row.requestedTotalUnit)} ширхэг`}
        />
      </Table.Summary.Cell>
      <Table.Summary.Cell align="right">
        <TwoLineText
          align="end"
          primary={<Text strong>{`${formatKg(sumBy(rows, (row) => row.totalWeight))} кг`}</Text>}
          secondary={<VolumeText value={sumBy(rows, (row) => row.totalVolume)} />}
        />
      </Table.Summary.Cell>
      <Table.Summary.Cell />
    </Table.Summary.Row>
  );

  if (products.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Бараа байхгүй байна."
        style={{ margin: '16px 0' }}
      />
    );
  }

  return (
    <Table
      className="return__product__table"
      size="small"
      columns={columns}
      dataSource={products}
      pagination={false}
      rowKey={(record, index) => record?._id ?? index}
      summary={renderSummary}
      scroll={{ x: 'max-content' }}
    />
  );
};

export default ReturnProductTable;
